import { Component, OnDestroy, OnInit, signal, ChangeDetectionStrategy, DestroyRef, effect, inject, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { ToolsService } from '@generated/services/tools.service';
import { NotificationService } from '@core/services/notification.service';
import { Signalr } from '@core/services/signalr';
import { ToolSummary, GetToolsResult } from '@generated/models';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { AssistantContextService } from '@shared/services/assistant-context.service';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';

@Component({
  selector: 'app-tools',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    AssistantActionDirective,
    FormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    MenuModule,
    TooltipModule,
    DeleteConfirmationComponent
],
  templateUrl: './tools.html',
  styleUrl: './tools.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToolsComponent implements OnInit, OnDestroy {
  tools = signal<ToolSummary[]>([]);
  filteredTools = signal<ToolSummary[]>([]);
  searchTerm = signal('');
  loading = signal(false);

  // Delete
  showDeleteDialog = signal(false);
  isDeleting = signal(false);
  toolToDelete: ToolSummary | null = null;

  readonly deleteTitle = $localize`:@@tools.delete.title:Delete Tool`;
  get deleteSubtitle(): string {
    const name = this.toolToDelete?.title || '';
    return $localize`:@@tools.delete.subtitle:Are you sure you want to delete '${name}:name:'? This action cannot be undone.`;
  }

  private router = inject(Router);
  private toolsService = inject(ToolsService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private signalr = inject(Signalr);
  private destroyRef = inject(DestroyRef);
  private assistantContext = inject(AssistantContextService);

  constructor() {
    effect(() => {
      if (this.loading()) return;
      const tools = this.tools();
      this.assistantContext.set({
        description: 'Tools list',
        value: {
          type: 'tools-list',
          count: tools.length,
          items: tools.map(t => ({ id: t.id, title: t.title })),
        },
      });
    });
  }

  ngOnInit() {
    this.loadTools();

    this.signalr.toolCreated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadTools());
  }

  ngOnDestroy(): void {
    this.assistantContext.clear();
  }

  loadTools() {
    this.loading.set(true);
    
    this.toolsService.apiToolsGet({ type: 'Webhook' }).subscribe({
      next: (result: GetToolsResult) => {
        const tools = result.tools || [];
        this.tools.set(tools);
        this.filteredTools.set(tools);
        this.loading.set(false);
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Error loading tools:', error);
        this.notificationService.showError($localize`:@@tools.notify.loadFail:Failed to load tools: ${error.message}:message:`);
        this.loading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  onSearch() {
    const term = this.searchTerm().toLowerCase();
    const filtered = this.tools().filter(tool => 
      (tool.title || '').toLowerCase().includes(term) ||
      (tool.description || '').toLowerCase().includes(term) ||
      (tool.created_by_email || '').toLowerCase().includes(term)
    );
    this.filteredTools.set(filtered);
  }

  addWebhookTool() {
    this.router.navigate(['/webhook-tools/new']);
  }

  deleteTool(tool: ToolSummary, event: Event) {
    event.stopPropagation();
    this.toolToDelete = tool;
    this.showDeleteDialog.set(true);
  }

  onDeleteConfirm() {
    if (!this.toolToDelete?.id) return;
    this.isDeleting.set(true);
    this.toolsService.apiToolsIdDelete({ id: this.toolToDelete.id }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@tools.delete.success:Tool deleted`);
        this.showDeleteDialog.set(false);
        this.isDeleting.set(false);
        this.toolToDelete = null;
        this.loadTools();
      },
      error: () => {
        this.notificationService.showError($localize`:@@tools.delete.error:Failed to delete tool`);
        this.isDeleting.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  getToolTypeIcon(tool: ToolSummary): string {
    switch (tool.type) {
      case 'System':
        return 'pi pi-cog';
      case 'Webhook':
        return 'pi pi-link';
      default:
        return 'pi pi-tool';
    }
  }
}